/**
 * Idiomas del producto.
 *
 * Se traduce por **idioma** (`es`, `en`) y se formatea por **región**
 * (`es-CO`, `es-AR`, `en-GB`). Mantener un catálogo de textos por país
 * multiplicaría el trabajo de traducción para diferencias que casi nadie nota
 * en un producto de almacenamiento; en cambio la fecha «14/8/2026» frente a
 * «8/14/2026» se nota a la primera, y eso lo resuelve `Intl` sin traducir nada.
 */
export const LOCALES = ['es', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'es';

/** Nombre de cada idioma **en ese idioma**: nadie busca «Spanish» en inglés. */
export const LOCALE_LABEL: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
};

/**
 * Extrae el idioma de una etiqueta BCP-47.
 *
 * Acepta lo que llega del navegador o de la base —`es-CO`, `ES`, `es_MX`,
 * `en-US`— y devuelve el idioma soportado, o `null` si no hay ninguno. Es
 * deliberadamente tolerante: `Accept-Language` y `navigator.language` no
 * garantizan formato, y rechazar por una mayúscula dejaría al usuario en el
 * idioma equivocado.
 */
export function parseLocale(value: unknown): Locale | null {
  if (typeof value !== 'string' || value.length === 0) return null;

  const idioma = value.trim().toLowerCase().replace('_', '-').split('-')[0];
  return LOCALES.find((locale) => locale === idioma) ?? null;
}

/**
 * Idioma a usar, en orden de prioridad.
 *
 * La elección explícita del usuario manda siempre sobre lo que diga el
 * navegador: quien cambia el idioma a mano espera que se respete aunque su
 * sistema esté en otro.
 */
export function resolveLocale(...candidates: unknown[]): Locale {
  for (const candidate of candidates) {
    const locale = parseLocale(candidate);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}
